import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Escape the sidebar's scrolling container without disabling sidebar scrolling.
export default function SidebarFlyout({ anchorRef, id, label, onClose, children }) {
  const panelRef = useRef(null);
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    const updatePosition = () => {
      const anchor = anchorRef.current;
      const panel = panelRef.current;
      if (!anchor || !panel) return;
      const bounds = anchor.getBoundingClientRect();
      const scroller = anchor.closest(".rbr-sidebar-top")?.getBoundingClientRect();
      if (window.innerWidth < 960 || (scroller && (bounds.bottom <= scroller.top || bounds.top >= scroller.bottom))) {
        onClose();
        return;
      }
      const panelBounds = panel.getBoundingClientRect();
      const margin = 12;
      setPosition({
        left: Math.max(margin, Math.min(bounds.right + 10, window.innerWidth - panelBounds.width - margin)),
        top: Math.max(margin, Math.min(bounds.top, window.innerHeight - panelBounds.height - margin)),
      });
    };
    const dismissOutside = (event) => {
      if (!panelRef.current?.contains(event.target) && !anchorRef.current?.contains(event.target)) onClose();
    };
    const dismissEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
        anchorRef.current?.focus();
      }
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    document.addEventListener("pointerdown", dismissOutside);
    document.addEventListener("keydown", dismissEscape);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updatePosition);
    if (panelRef.current) observer?.observe(panelRef.current);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      document.removeEventListener("pointerdown", dismissOutside);
      document.removeEventListener("keydown", dismissEscape);
      observer?.disconnect();
    };
  }, [anchorRef, onClose]);

  useLayoutEffect(() => {
    panelRef.current?.querySelector("a, button")?.focus();
  }, []);

  return createPortal(
    <nav id={id} ref={panelRef} className="sidebar-flyout" aria-label={label}
      style={{ ...position, visibility: position ? "visible" : "hidden" }}>
      <div className="sidebar-flyout-title">{label}</div>
      <ul className="sidebar-flyout-list">{children}</ul>
    </nav>,
    document.body,
  );
}
